import { BRAND_CLASSES } from '@/constants/theme'

export interface MultiSelectOption {
    value: string | number
    label: string
    /** When true, this option can't be toggled — it renders in its current checked state but locked. */
    disabled?: boolean
}

export interface MultiSelectProps {
    /** Field label */
    label?: string
    /** Whether field is required */
    required?: boolean
    /** Error message to display */
    error?: string
    /** Helper text to display below select */
    helperText?: string
    /** Array of available options */
    options: MultiSelectOption[]
    /** Array of currently selected values */
    value: (string | number)[]
    /** Callback when selection changes */
    onChange: (selected: (string | number)[]) => void
    /** Whether data is loading */
    isLoading?: boolean
    /** Custom class name for container */
    containerClassName?: string
    /** Message to display when no options available */
    emptyMessage?: string
    /** Optional action to show when empty */
    emptyAction?: {
        label: string
        onClick: () => void
    }
    /** Maximum height for scrollable area (default: 10rem / 160px) */
    maxHeight?: string
    /** Disable the entire control — every checkbox is locked. */
    disabled?: boolean
}

/**
 * Common MultiSelect component for selecting multiple items via checkboxes
 * Does NOT use React Hook Form - manages its own state via value/onChange
 *
 * @example
 * ```tsx
 * const [selectedIds, setSelectedIds] = useState<string[]>([])
 *
 * <MultiSelect
 *   label="Audiences"
 *   options={audiences.map(a => ({ value: a.Id, label: a.Name }))}
 *   value={selectedIds}
 *   onChange={setSelectedIds}
 *   isLoading={isLoading}
 *   helperText="Select multiple to aggregate contacts"
 *   emptyMessage="No audiences yet."
 *   emptyAction={{
 *     label: 'Create one',
 *     onClick: handleCreateAudience
 *   }}
 * />
 * ```
 */
export function MultiSelect({
    label,
    required,
    error,
    helperText,
    options,
    value,
    onChange,
    isLoading,
    containerClassName,
    emptyMessage = 'No options available',
    emptyAction,
    maxHeight = '10rem',
    disabled = false
}: MultiSelectProps) {
    const handleToggle = (optionValue: string | number) => {
        if (disabled) return
        const option = options.find(o => o.value === optionValue)
        if (option?.disabled) return
        const isSelected = value.includes(optionValue)
        if (isSelected) {
            onChange(value.filter(v => v !== optionValue))
        } else {
            onChange([...value, optionValue])
        }
    }

    return (
        <div className={containerClassName}>
            {label && (
                <label className={`block text-sm font-medium mb-2 ${BRAND_CLASSES.INPUT.LABEL}`}>
                    {label}
                    {required && <span className={BRAND_CLASSES.INPUT.REQUIRED}> *</span>}
                </label>
            )}

            <div className="space-y-2">
                {isLoading && (
                    <div className={`text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>
                        Loading...
                    </div>
                )}

                {!isLoading && options.length === 0 && (
                    <div className={`text-xs ${BRAND_CLASSES.INPUT.HELPER} flex items-center gap-2`}>
                        <span>{emptyMessage}</span>
                        {emptyAction && (
                            <button
                                type="button"
                                onClick={emptyAction.onClick}
                                className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                            >
                                {emptyAction.label}
                            </button>
                        )}
                    </div>
                )}

                {!isLoading && options.length > 0 && (
                    <div
                        className={`
                            border rounded-lg p-2 overflow-auto space-y-1
                            ${error ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'}
                            ${BRAND_CLASSES.INPUT.NORMAL_BG}
                        `}
                        style={{ maxHeight }}
                    >
                        {options.map(option => {
                            const isChecked = value.includes(option.value)
                            // Locked when the whole control is disabled or the option opts out of toggling.
                            const optionLocked = disabled || !!option.disabled
                            return (
                                <label
                                    key={option.value}
                                    className={`flex items-center gap-2 text-xs select-none rounded px-1 py-0.5 transition-colors ${
                                        optionLocked
                                            ? 'cursor-not-allowed text-slate-400 dark:text-slate-500'
                                            : 'cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        className="rounded border-slate-300 dark:border-slate-500 text-primary-600 focus:ring-primary-500 disabled:opacity-60"
                                        checked={isChecked}
                                        disabled={optionLocked}
                                        onChange={() => handleToggle(option.value)}
                                    />
                                    <span className="truncate">{option.label}</span>
                                </label>
                            )
                        })}
                    </div>
                )}

                {error && (
                    <p className={`text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
                        {error}
                    </p>
                )}

                {!error && helperText && (
                    <p className={`text-[11px] ${BRAND_CLASSES.INPUT.HELPER}`}>
                        {helperText}
                    </p>
                )}
            </div>
        </div>
    )
}
